import { Dispatch } from 'redux'
import { nanoid } from 'nanoid'

import {
  destroy,
  editData,
  getOne,
  update
} from 'redux/api/Widgets/ReportWidget'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { useThunkActions } from 'redux/actions/useThunkActions'

import * as actions from './actions'

import { parseApiError } from 'utils/errorParser'
import { InnerWidgetPatchBody } from 'types/GlobalWidget'
import { SnackbarMessage } from 'types/GlobalSnackbar'
import { toggleSnackbarOpen } from 'redux/actions/Snackbar'
import { Action } from 'types/GlobalUserEvents'
import { TEditLockedWidgetBody } from 'types/GlobalReport'
import { QueryParams } from 'redux/api/Widgets'

function tryGetOneReportWidget(
  id: string,
  reportId: string,
  queryParams: QueryParams | null
) {
  return (dispatch: Dispatch) => {
    const fetchId = nanoid()

    dispatch(actions.getOne({ id: id, fetchId }))

    getOne(id, reportId, queryParams)
      .then((response) => {
        dispatch(actions.getOneSuccess({ data: response.data.data, fetchId }))
      })
      .catch((error) => {
        const errorDetails = parseApiError(error)

        dispatch(
          actions.getOneFailed({
            widgetId: id,
            fetchId,
            error: errorDetails
          })
        )
      })
  }
}

function tryDeleteReportWidget(
  id: string,
  reportId: string,
  callback: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id, reportId)
      .then((response) => {
        dispatch(
          actions.destroySuccess({
            id,
            reportId,
            deletedBlockId: response.data.data.block_id
          })
        )
        callback()
      })
      .catch(() => {
        dispatch(actions.destroyFailed())
        callback(true)
      })
  }
}

function tryUpdateReportWidget(
  id: string,
  reportId: string,
  body: InnerWidgetPatchBody,
  periodFilterIsSet = false
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, reportId, body)
      .then((response) => {
        UserEvents.log({
          action: Action.WIDGET_EDITED,
          context: null,
          dashboard_id: null,
          dashboard_group_id: null,
          report_id: null
        })

        dispatch(
          actions.updateSuccess({
            data: response.data.data,
            // skipFetchDataOnUpdate(body, periodFilterIsSet) #TODO
            skipFetchData: periodFilterIsSet
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdated))
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdatedFailed))
      })
  }
}

function tryEditDataReportWidget(
  id: string,
  editionId: string,
  body: TEditLockedWidgetBody
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.editDataPending({ id }))

    editData(id, editionId, body)
      .then((response) => {
        dispatch(
          actions.updateSuccess({
            data: response.data.data,
            skipFetchData: true
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdated))
      })
      .catch(() => {
        dispatch(actions.editDataFailed({ id }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdatedFailed))
      })
  }
}

export const useReportWidgetActions = () => {
  return {
    tryEditDataReportWidget: useThunkActions(tryEditDataReportWidget),
    tryGetOneReportWidget: useThunkActions(tryGetOneReportWidget),
    tryDeleteReportWidget: useThunkActions(tryDeleteReportWidget),
    tryUpdateReportWidget: useThunkActions(tryUpdateReportWidget)
  }
}
